import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as moment from 'moment';
import PropTypes from 'prop-types';
import { WALLET_TYPE } from '../../../../proptype/walletProp';
import translate from '../../../../../../commonComponents/translate';
import ReactLoader from '../../../../../../commonComponents/loader/ReactLoader';
import AmColumnAndLineChart from '../../../../../../commonComponents/charts/AmColumnAndLineChart';

const FILTER = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
};

const FILTERS = Object.values(FILTER);

const fetchCoinsInformation = async (t, type, filters = {}) => {
  const { data: response } = await axios.get(
    `/api/analytics/wallet/evolution/${WALLET_TYPE.COINS}/${type}`,
    {
      params: filters,
    },
  );

  return response.data.map(coinItem => {
    const { year, month, week, day } = coinItem;
    let category;
    switch (type) {
      case FILTER.YEAR:
        category = year;
        break;
      case FILTER.MONTH:
        category = `${moment()
          .month(month - 1)
          .format('MMM')} ${year}`;
        break;
      case FILTER.WEEK:
        category = `${t('chartPortlet.week')}: ${week}, ${year}`;
        break;
      case FILTER.DAY:
      default:
        category = `${day}/${month}/${year}`;
        break;
    }
    return {
      ...coinItem,
      category,
    };
  });
};

function CoinsEvolution(props) {
  const { t, filters } = props;
  const [coinsEvolution, setCoinsEvolution] = useState({});
  const [activeFilter, setActiveFilter] = useState(FILTER.DAY);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const chartData = await fetchCoinsInformation(t, FILTER.DAY, filters);
      setCoinsEvolution(chartData);
      setIsLoading(false);
    };

    fetchData();
  }, [filters]);

  const handleBtnClick = useCallback(
    async type => {
      setIsLoading(true);
      setActiveFilter(type);
      const chartData = await fetchCoinsInformation(t, type, filters);
      setCoinsEvolution(chartData);
      setIsLoading(false);
    },
    [setActiveFilter, t, fetchCoinsInformation, setCoinsEvolution, filters],
  );

  const getBtnClass = useCallback(
    btn => `filter-btn ${activeFilter === btn ? 'filter-btn-active' : ''}`,
    [activeFilter],
  );

  return (
    <div className="wallet-coins-evolution-container">
      <div className="header-container">
        <h5>{t('coins.evolution')}</h5>
        <div className="filters-coin-evolution-wrapper">
          {FILTERS.map(btn => (
            <button
              key={btn}
              className={getBtnClass(btn)}
              type="button"
              disabled={btn === activeFilter || isLoading}
              onClick={() => handleBtnClick(btn)}
            >
              {t(`chartPortlet.${btn}`)}
            </button>
          ))}
        </div>
      </div>
      <div>
        {Object.values(coinsEvolution)?.length > 0 ? (
          <ReactLoader isLoading={isLoading}>
            <AmColumnAndLineChart
              containerClass="wallet-coins-evolution"
              options={{
                category: 'category',
                yLabel: '', // Y TEXT,
                columnValueKey: 'total',
                columnSeriesName: t('rewards_coins'),
                valueAxisLimits: { min: 0 },
                colorRect: '#F5D509',
              }}
              chartId="walletCoinsEvolutionChart"
              stats={coinsEvolution}
            />
          </ReactLoader>
        ) : (
          <div className="wallet-coins-evolution">
            <p className="no-data-hint">{t('no.history.data')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

CoinsEvolution.propTypes = {
  t: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    startDate: PropTypes.number,
    endDate: PropTypes.number,
  }).isRequired,
};

export default translate(CoinsEvolution);
